import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { WorkingProfile, PolicyViolationSettings, PolicyLimits, PolicyCheckType, ApprovalScreenSettings } from '@/types/timesheetSettings';
import { HelpCircle, Clock, ShieldAlert, Monitor } from 'lucide-react';
import { PolicyCheckCard, type PolicyCheckConfig, type PolicySeverity } from './PolicyCheckCard';

/** Map legacy / non-standard severity strings to the canonical low | medium | high values. */
function normaliseSeverity(raw: string | undefined, fallback: PolicySeverity): PolicySeverity {
  if (raw === 'low' || raw === 'medium' || raw === 'high') return raw;
  if (raw === 'warning') return 'medium';
  if (raw === 'info') return 'low';
  if (raw === 'critical' || raw === 'error') return 'high';
  return fallback;
}

interface WorkingHoursConfigurationCompactProps {
  workingProfile: WorkingProfile;
  policyEnforcement: PolicyViolationSettings;
  policyLimits: PolicyLimits | undefined;
  approvalScreen: ApprovalScreenSettings;
  onWorkingProfileChange: (profile: WorkingProfile) => void;
  onPolicyEnforcementChange: (enforcement: PolicyViolationSettings) => void;
  onPolicyLimitsChange: (limits: PolicyLimits | undefined) => void;
  onApprovalScreenChange: (settings: ApprovalScreenSettings) => void;
}

export function WorkingHoursConfigurationCompact({
  workingProfile,
  policyEnforcement,
  policyLimits,
  approvalScreen,
  onWorkingProfileChange,
  onPolicyEnforcementChange,
  onPolicyLimitsChange,
  onApprovalScreenChange,
}: WorkingHoursConfigurationCompactProps) {
  const [weeklyHoursInput, setWeeklyHoursInput] = React.useState<string>(workingProfile.standardHoursPerWeek.toFixed(2));
  const [dailyHoursInputs, setDailyHoursInputs] = React.useState<Record<string, string>>({});

  // Per-mode alert configurations - Day and Week maintain fully independent settings
  const [modeConfigs, setModeConfigs] = React.useState<Record<'day' | 'week', {
    excessiveHours: PolicyCheckConfig;
    missingHours: PolicyCheckConfig;
    nonWorkingDay: PolicyCheckConfig;
    legalMaximum: PolicyCheckConfig;
  }>>(() => {
    const dayOverage = policyEnforcement.checks['daily-overage'];
    const weekOverage = policyEnforcement.checks['weekly-overage'];
    const dayMissingHours = policyEnforcement.checks['daily-missing-hours'];
    const weekMissingHours = policyEnforcement.checks['weekly-missing-hours'];
    const dayNonWorkingDay = policyEnforcement.checks['daily-non-working-day'];
    const weekNonWorkingDay = policyEnforcement.checks['weekly-non-working-day'];
    const legalDaily = policyEnforcement.checks['legal-daily-maximum'];
    const legalWeekly = policyEnforcement.checks['legal-weekly-maximum'];

    return {
      day: {
        excessiveHours: {
          enabled: dayOverage?.enabled ?? true,
          threshold: dayOverage?.threshold ?? 1,
          severity: normaliseSeverity(dayOverage?.severity, 'medium'),
          enforcement: dayOverage?.enforcement ?? 'warn',
        },
        missingHours: {
          enabled: dayMissingHours?.enabled ?? true,
          threshold: dayMissingHours?.threshold ?? 2,
          severity: normaliseSeverity(dayMissingHours?.severity, 'low'),
          enforcement: dayMissingHours?.enforcement ?? 'warn',
        },
        nonWorkingDay: {
          enabled: dayNonWorkingDay?.enabled ?? true,
          threshold: 0,
          severity: normaliseSeverity(dayNonWorkingDay?.severity, 'low'),
          enforcement: dayNonWorkingDay?.enforcement ?? 'warn',
        },
        legalMaximum: {
          enabled: legalDaily?.enabled ?? false,
          threshold: policyLimits?.legalMaxHoursPerDay ?? 13,
          severity: legalDaily?.severity ?? 'high',
          enforcement: 'block',
        },
      },
      week: {
        excessiveHours: {
          enabled: weekOverage?.enabled ?? true,
          threshold: weekOverage?.threshold ?? 2,
          severity: normaliseSeverity(weekOverage?.severity, 'medium'),
          enforcement: weekOverage?.enforcement ?? 'warn',
        },
        missingHours: {
          enabled: weekMissingHours?.enabled ?? true,
          threshold: weekMissingHours?.threshold ?? 8,
          severity: normaliseSeverity(weekMissingHours?.severity, 'low'),
          enforcement: weekMissingHours?.enforcement ?? 'warn',
        },
        nonWorkingDay: {
          enabled: weekNonWorkingDay?.enabled ?? true,
          threshold: 0,
          severity: normaliseSeverity(weekNonWorkingDay?.severity, 'low'),
          enforcement: weekNonWorkingDay?.enforcement ?? 'warn',
        },
        legalMaximum: {
          enabled: legalWeekly?.enabled ?? false,
          threshold: policyLimits?.legalMaxHoursPerWeek ?? 48,
          severity: legalWeekly?.severity ?? 'high',
          enforcement: 'block',
        },
      },
    };
  });

  // Derive current mode's configs
  const currentMode = policyEnforcement.monitoringFrequency;
  const excessiveHoursConfig = modeConfigs[currentMode].excessiveHours;
  const missingHoursConfig = modeConfigs[currentMode].missingHours;
  const nonWorkingDayConfig = modeConfigs[currentMode].nonWorkingDay;
  const legalMaximumConfig = modeConfigs[currentMode].legalMaximum;

  // Sync legal limits threshold when policyLimits changes externally
  React.useEffect(() => {
    setModeConfigs(prev => ({
      ...prev,
      day: {
        ...prev.day,
        legalMaximum: {
          ...prev.day.legalMaximum,
          threshold: policyLimits?.legalMaxHoursPerDay ?? 13,
        },
      },
      week: {
        ...prev.week,
        legalMaximum: {
          ...prev.week.legalMaximum,
          threshold: policyLimits?.legalMaxHoursPerWeek ?? 48,
        },
      },
    }));
  }, [policyLimits]);

  // Update local input when workingProfile changes externally
  React.useEffect(() => {
    setWeeklyHoursInput(workingProfile.standardHoursPerWeek.toFixed(2));
  }, [workingProfile.standardHoursPerWeek]);

  // Initialize daily hours inputs
  React.useEffect(() => {
    const inputs: Record<string, string> = {};
    Object.entries(workingProfile.workingDays).forEach(([key, dayData]) => {
      inputs[key] = dayData.expectedHours.toFixed(2);
    });
    setDailyHoursInputs(inputs);
  }, [workingProfile.workingDays]);

  // Calculate working days and daily hours
  const workingDays = Object.values(workingProfile.workingDays).filter(d => d.isWorkingDay);
  const avgDailyHours = workingDays.length > 0 
    ? workingProfile.standardHoursPerWeek / workingDays.length 
    : 8;

  // Quick preset handler (hours only)
  const applyQuickHours = (hours: number) => {
    // Get current working days
    const currentWorkingDays = Object.entries(workingProfile.workingDays)
      .filter(([_, dayData]) => dayData.isWorkingDay);
    
    if (currentWorkingDays.length === 0) {
      // If no working days, just update the weekly hours
      onWorkingProfileChange({
        ...workingProfile,
        standardHoursPerWeek: hours,
      });
      return;
    }

    // Calculate new daily hours
    const newDailyHours = hours / currentWorkingDays.length;
    
    // Update working days with new hours (only for non-custom days)
    const updatedWorkingDays = { ...workingProfile.workingDays };
    currentWorkingDays.forEach(([dayKey, dayData]) => {
      if (!dayData.isCustom) {
        updatedWorkingDays[dayKey as keyof typeof workingProfile.workingDays] = {
          ...dayData,
          expectedHours: newDailyHours,
        };
      }
    });

    onWorkingProfileChange({
      ...workingProfile,
      standardHoursPerWeek: hours,
      workingDays: updatedWorkingDays,
    });
  };

  // Quick preset handler with days
  const applyPreset = (hours: number, days: string[]) => {
    const dailyHours = hours / days.length;
    const workingDaysConfig: typeof workingProfile.workingDays = {
      monday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      tuesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      wednesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      thursday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      friday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      saturday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      sunday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
    };

    days.forEach((day) => {
      workingDaysConfig[day as keyof typeof workingDaysConfig] = {
        isWorkingDay: true,
        expectedHours: dailyHours,
        isCustom: false,
      };
    });

    onWorkingProfileChange({
      standardHoursPerWeek: hours,
      workingDays: workingDaysConfig,
    });
  };

  // Policy check handlers
  const toggleCheck = (checkType: PolicyCheckType, enabled: boolean) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          enabled,
        },
      },
      preset: 'custom',
    });
  };

  const updateThreshold = (checkType: PolicyCheckType, threshold: number) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          threshold,
        },
      },
      preset: 'custom',
    });
  };

  const updateSeverity = (checkType: PolicyCheckType, severity: 'low' | 'medium' | 'high') => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          severity,
        },
      },
      preset: 'custom',
    });
  };

  // Legal limits handlers
  const toggleLegalLimits = (enabled: boolean) => {
    if (enabled) {
      onPolicyLimitsChange({
        legalMaxHoursPerDay: 13,
        legalMaxHoursPerWeek: 48,
      });
    } else {
      onPolicyLimitsChange(undefined);
    }
  };

  const updateLegalLimit = (field: 'legalMaxHoursPerDay' | 'legalMaxHoursPerWeek', value: number) => {
    if (policyLimits) {
      onPolicyLimitsChange({
        ...policyLimits,
        [field]: value,
      });
    }
  };

  return (
    <TooltipProvider>
      <Accordion type="multiple" defaultValue={["standard-hours", "policy-checks", "approval-screen"]} className="space-y-4">

        {/* ── Standard Hours ───────────────────────────────────────────── */}
        <AccordionItem value="standard-hours" className="border rounded-lg">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <div className="font-semibold">Standard Hours</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Define standard working hours and configure policy checks
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2 pb-4">
            {/* Combined Weekly Hours and Days Grid */}
            <div className="p-4 bg-background rounded-lg border space-y-4">
              {/* Weekly Hours - Read Only Plain Text */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Total:</span>
                <span className="text-sm font-semibold">{weeklyHoursInput}h</span>
                <span className="text-xs text-muted-foreground">/ week</span>
              </div>

              {/* Days and Hours Grid */}
              <div className="grid grid-cols-7 gap-3">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                  const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index] as keyof typeof workingProfile.workingDays;
                  const dayData = workingProfile.workingDays[dayKey];

                  return (
                    <div key={day} className="flex flex-col items-center gap-1">
                      <Label className={`text-xs font-medium ${!dayData.isWorkingDay ? 'text-muted-foreground/50' : ''}`}>{day}</Label>
                      <span className={`text-sm font-semibold ${!dayData.isWorkingDay ? 'text-muted-foreground/40' : ''}`}>
                        {dayData.isWorkingDay ? `${dayData.expectedHours.toFixed(2)}h` : '-'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ── Policy Checks ────────────────────────────────────────────── */}
        <AccordionItem value="policy-checks" className="border rounded-lg">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <ShieldAlert className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <div className="font-semibold">Policy Checks</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Configure alerts for policy violations
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
        {/* Policy Checks Content */}
        <div className="space-y-3">

          {/* Monitoring Frequency */}
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
            <Label className="text-sm flex-shrink-0 w-32">Check Hours by</Label>
            <div className="flex rounded-lg border overflow-hidden">
              {(['day', 'week'] as const).map((freq) => (
                <label
                  key={freq}
                  className={`px-4 py-1.5 text-sm font-medium transition-all duration-150 border-r last:border-r-0 cursor-pointer ${
                    policyEnforcement.monitoringFrequency === freq
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-accent/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="monitoringFrequency"
                    value={freq}
                    checked={policyEnforcement.monitoringFrequency === freq}
                    onChange={() => {
                      onPolicyEnforcementChange({
                        ...policyEnforcement,
                        monitoringFrequency: freq,
                      });
                      // After re-render, ensure ancestor containers haven't
                      // scrolled. The inner overflow-auto div in SettingsPage
                      // handles all scrolling; <main> and above must stay at
                      // scrollTop 0. Without this, browser focus-management
                      // may scroll <main>, creating white space below the app
                      // when running inside an iframe (e.g. Access platform).
                      requestAnimationFrame(() => {
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                        const main = document.querySelector('main');
                        if (main) main.scrollTop = 0;
                      });
                    }}
                    className="sr-only"
                  />
                  {freq === 'day' ? 'Day' : 'Week'}
                </label>
              ))}
            </div>
          </div>

          {/* Policy Check Cards */}
          <div className="space-y-4">
            <PolicyCheckCard
              title="Excessive Hours Alert"
              config={excessiveHoursConfig}
              onChange={(config: PolicyCheckConfig) => {
                setModeConfigs(prev => ({
                  ...prev,
                  [currentMode]: { ...prev[currentMode], excessiveHours: config },
                }));
                const checkType = currentMode === 'week' ? 'weekly-overage' : 'daily-overage';
                onPolicyEnforcementChange({
                  ...policyEnforcement,
                  checks: {
                    ...policyEnforcement.checks,
                    [checkType]: {
                      enabled: config.enabled,
                      threshold: config.threshold,
                      severity: config.severity,
                      enforcement: config.enforcement,
                    },
                  },
                  preset: 'custom',
                });
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
            />

            <PolicyCheckCard
              title="Missing Hours Alert"
              config={missingHoursConfig}
              onChange={(config: PolicyCheckConfig) => {
                setModeConfigs(prev => ({
                  ...prev,
                  [currentMode]: { ...prev[currentMode], missingHours: config },
                }));
                const checkType = currentMode === 'week' ? 'weekly-missing-hours' : 'daily-missing-hours';
                onPolicyEnforcementChange({
                  ...policyEnforcement,
                  checks: {
                    ...policyEnforcement.checks,
                    [checkType]: {
                      enabled: config.enabled,
                      threshold: config.threshold,
                      severity: config.severity,
                      enforcement: config.enforcement,
                    },
                  },
                  preset: 'custom',
                });
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isMissingHours={true}
            />

            <PolicyCheckCard
              title="Non-Working Day Entry"
              config={nonWorkingDayConfig}
              onChange={(config: PolicyCheckConfig) => {
                setModeConfigs(prev => ({
                  ...prev,
                  [currentMode]: { ...prev[currentMode], nonWorkingDay: config },
                }));
                const checkType = currentMode === 'week' ? 'weekly-non-working-day' : 'daily-non-working-day';
                onPolicyEnforcementChange({
                  ...policyEnforcement,
                  checks: {
                    ...policyEnforcement.checks,
                    [checkType]: {
                      enabled: config.enabled,
                      severity: config.severity,
                      enforcement: config.enforcement,
                    },
                  },
                  preset: 'custom',
                });
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isNonWorkingDay={true}
            />

            {/* Divider between soft checks and legal maximum */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">
                  Legal Maximum
                </span>
              </div>
            </div>

            <PolicyCheckCard
              title="Legal Maximum (Optional)"
              config={legalMaximumConfig}
              onChange={(config: PolicyCheckConfig) => {
                setModeConfigs(prev => ({
                  ...prev,
                  [currentMode]: { ...prev[currentMode], legalMaximum: config },
                }));
                // Update parent policy enforcement state and legal limits
                const checkType = policyEnforcement.monitoringFrequency === 'week' ? 'legal-weekly-maximum' : 'legal-daily-maximum';
                onPolicyEnforcementChange({
                  ...policyEnforcement,
                  checks: {
                    ...policyEnforcement.checks,
                    [checkType]: {
                      enabled: config.enabled,
                      severity: config.severity,
                    },
                  },
                  preset: 'custom',
                });
                
                // Update legal limits based on enabled state and threshold
                if (config.enabled) {
                  if (policyEnforcement.monitoringFrequency === 'week') {
                    onPolicyLimitsChange({
                      legalMaxHoursPerDay: policyLimits?.legalMaxHoursPerDay || 13,
                      legalMaxHoursPerWeek: config.threshold,
                    });
                  } else {
                    onPolicyLimitsChange({
                      legalMaxHoursPerDay: config.threshold,
                      legalMaxHoursPerWeek: policyLimits?.legalMaxHoursPerWeek || 48,
                    });
                  }
                } else {
                  // If disabled, clear the appropriate limit
                  if (policyEnforcement.monitoringFrequency === 'week') {
                    if (policyLimits?.legalMaxHoursPerDay) {
                      onPolicyLimitsChange({
                        legalMaxHoursPerDay: policyLimits.legalMaxHoursPerDay,
                        legalMaxHoursPerWeek: 0,
                      });
                    } else {
                      onPolicyLimitsChange(undefined);
                    }
                  } else {
                    if (policyLimits?.legalMaxHoursPerWeek) {
                      onPolicyLimitsChange({
                        legalMaxHoursPerDay: 0,
                        legalMaxHoursPerWeek: policyLimits.legalMaxHoursPerWeek,
                      });
                    } else {
                      onPolicyLimitsChange(undefined);
                    }
                  }
                }
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isLegalMaximum={true}
            />
          </div>
        </div>
          </AccordionContent>
        </AccordionItem>

        {/* ── Approval Screen ───────────────────────────────────────────── */}
        <AccordionItem value="approval-screen" className="border rounded-lg">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Monitor className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <div className="font-semibold">Approval Screen</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Control how issues and violations appear when a manager reviews a timesheet
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Issues panel default state */}
              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Open issues panel by default</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">When enabled, the Issues panel will open automatically when a manager opens a timesheet for review. When disabled (recommended), managers click the Issues button to view them.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {approvalScreen.panelDefaultExpanded
                      ? 'Issues panel opens automatically — the timesheet grid will be below the fold'
                      : 'Recommended — managers see the timesheet grid immediately and open issues on demand'}
                  </p>
                </div>
                <Checkbox
                  checked={approvalScreen.panelDefaultExpanded}
                  onCheckedChange={(val) => onApprovalScreenChange({ ...approvalScreen, panelDefaultExpanded: !!val })}
                />
              </div>

              {/* Minimum severity filter */}
              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Minimum severity to show</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">Filter which severity levels are shown in the approval review issues panel. Lower severity issues will be hidden from managers during approval.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Issues below this severity will be hidden from the approval review panel
                  </p>
                </div>
                <Select
                  value={approvalScreen.minSeverityToShow}
                  onValueChange={(val) => onApprovalScreenChange({ ...approvalScreen, minSeverityToShow: val as 'all' | 'medium' | 'high' })}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Low, Medium, High)</SelectItem>
                    <SelectItem value="medium">Medium &amp; High only</SelectItem>
                    <SelectItem value="high">High (Errors) only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </TooltipProvider>
  );
}
